/**
 * UranIA PA — Onboarding PA stepper UI (US-015).
 *
 * Route `/onboarding/pa` per il Funzionario AdG (analisi §10 BC
 * Onboarding + CLAUDE.md L78 distinzione PA vs R&I). Implementa i 5 step
 * della wizard PA:
 *   1. Anagrafica (nome, cognome, codice_fiscale, telefono opz.)
 *   2. PO di riferimento (es. OP1/OP2/...)
 *   3. Fondo principale (FESR / FSE+)
 *   4. Ruolo nella AdG + esperienza_anni
 *   5. Consent privacy + AI Act art. 50 (AC US-015 #4)
 *
 * Acceptance Criteria coperti:
 *   - AC#1 — stepper Bootstrap Italia (`OnboardingStepper` riusato anche
 *     da US-016 R&I).
 *   - AC#2 — React Hook Form + zodResolver, schemi Zod allineati 1:1 a
 *     `OnboardingPaStep{1..5}*` dell'OAS v0.4.0.
 *   - AC#3 — ogni step transition fa POST a `/api/v1/onboarding/pa/sessions/
 *     {id}/steps/{n}`; cache reload-safe via `pa-session-storage` (
 *     sessionStorage chiavata per subject); la source-of-truth resta il
 *     backend (`current_step` ritornato dallo step response).
 *   - AC#4 — entrambi i consent boolean nello step 5 sono `z.literal(true)`,
 *     altrimenti la finalize POST non parte.
 *   - AC#5 — axe-core 0 serious (verificato da `spa-onboarding-pa-axe.spec.ts`).
 *   - AC#6 — typecheck (tsc -b strict) verde.
 *   - AC#7 — dev-browser fallback Playwright chromium (CLAUDE.md §Step 3).
 *
 * Pre-condizioni di accesso:
 *   - utente autenticato Keycloak (auth-store popolato);
 *   - claim realm role `funzionario_adg` (server-side enforcement —
 *     US-013); lato SPA mostriamo CTA "Vai al login" se mancano sessione.
 *
 * NB step-up MFA RFC 9470 (acr ∈ {L2, L3, CIE, fido2}) e' verificato
 * server-side (US-013): in caso di 403 con `WWW-Authenticate: step-up`
 * il client SPA dovra' riavviare login con `acr_values` opportuno
 * (US-022+ implementera' l'auto-redirect; in Fase 1 mostriamo errore
 * inline italian-first che istruisce l'utente).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { FieldValues, Resolver, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { JSX, ReactElement } from 'react';
import type { ZodType } from 'zod';

import { selectIsAuthenticated, selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import {
  createOnboardingPaSession,
  OnboardingApiError,
  submitOnboardingPaStep,
  type OnboardingPaStepBody,
} from '../../lib/onboarding/pa-api.ts';
import {
  FONDO_PRINCIPALE_VALUES,
  ONBOARDING_PA_TOTAL_STEPS,
  onboardingPaStep1Schema,
  onboardingPaStep2Schema,
  onboardingPaStep3Schema,
  onboardingPaStep4Schema,
  onboardingPaStep5Schema,
  type OnboardingPaSessionDto,
  type OnboardingPaStep1Body,
  type OnboardingPaStep2Body,
  type OnboardingPaStep3Body,
  type OnboardingPaStep4Body,
  type OnboardingPaStep5Body,
  type OnboardingPaStepNumber,
  type OnboardingPaStepResponseDto,
} from '../../lib/onboarding/pa-schemas.ts';
import {
  clearOnboardingPaSnapshot,
  loadOnboardingPaSnapshot,
  saveOnboardingPaSnapshot,
  snapshotFromSessionDto,
} from '../../lib/onboarding/pa-session-storage.ts';
import {
  OnboardingStepper,
  type OnboardingStepperStep,
} from '../../components/onboarding/OnboardingStepper.tsx';
import { Route as ServiziSegmentRoute } from '../_servizi.tsx';

/**
 * Costruisce un `Resolver<T>` da uno schema Zod v4 in modo type-erasure-safe.
 *
 * Motivazione: con `exactOptionalPropertyTypes` + Zod 4 + @hookform/resolvers 5,
 * gli overload di `zodResolver` non riescono a inferire un Input
 * `extends FieldValues` quando lo schema usa `z.coerce` / `z.literal(true)`
 * (input type degrada a `unknown`). Castiamo qui in un unico punto cosi'
 * il resto del file resta strictly-typed.
 */
function buildResolver<T extends FieldValues>(schema: ZodType): Resolver<T> {
  return zodResolver(schema as never) as Resolver<T>;
}

interface StepFormBaseProps<TBody extends FieldValues> {
  readonly defaultValues: Partial<TBody>;
  readonly onSubmitStep: (body: TBody) => Promise<void>;
  readonly onBack: (() => void) | null;
  readonly submitting: boolean;
}

function StepCard(props: { readonly title: string; readonly children: ReactElement }): JSX.Element {
  return (
    <section className="card shadow-sm" aria-labelledby="onboarding-pa-step-heading">
      <div className="card-body">
        <h2 id="onboarding-pa-step-heading" className="h4 mb-3">
          {props.title}
        </h2>
        {props.children}
      </div>
    </section>
  );
}

function StepNav(props: {
  readonly onBack: (() => void) | null;
  readonly submitting: boolean;
  readonly nextLabel: string;
  readonly backLabel: string;
}): JSX.Element {
  return (
    <div className="d-flex justify-content-between mt-4 gap-2">
      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={props.onBack ?? undefined}
        disabled={props.onBack === null || props.submitting}
        data-testid="onboarding-pa-back"
      >
        {props.backLabel}
      </button>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={props.submitting}
        data-testid="onboarding-pa-next"
      >
        {props.submitting ? `${props.nextLabel}…` : props.nextLabel}
      </button>
    </div>
  );
}

function Step1Anagrafica(props: StepFormBaseProps<OnboardingPaStep1Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingPaStep1Body>(onboardingPaStep1Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingPaStep1Body>({
    resolver,
    defaultValues,
  });

  const submit: SubmitHandler<OnboardingPaStep1Body> = async (data) => {
    const trimmedTelefono = typeof data.telefono === 'string' ? data.telefono.trim() : '';
    const normalized: OnboardingPaStep1Body = {
      nome: data.nome,
      cognome: data.cognome,
      codice_fiscale: data.codice_fiscale.toUpperCase(),
      ...(trimmedTelefono.length > 0 ? { telefono: trimmedTelefono } : {}),
    };
    await onSubmitStep(normalized);
  };

  return (
    <StepCard title={t('onboarding.pa.steps.s1.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(submit)(event);
        }}
        data-testid="onboarding-pa-form-step-1"
      >
        <div className="form-group">
          <label htmlFor="onboarding-pa-nome" className="form-label">
            {t('onboarding.pa.fields.nome')}
          </label>
          <input
            id="onboarding-pa-nome"
            type="text"
            className="form-control"
            autoComplete="given-name"
            aria-invalid={errors.nome !== undefined}
            aria-describedby={errors.nome !== undefined ? 'onboarding-pa-nome-error' : undefined}
            {...register('nome')}
          />
          {errors.nome !== undefined && (
            <small id="onboarding-pa-nome-error" className="form-text text-danger">
              {t('onboarding.pa.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-pa-cognome" className="form-label">
            {t('onboarding.pa.fields.cognome')}
          </label>
          <input
            id="onboarding-pa-cognome"
            type="text"
            className="form-control"
            autoComplete="family-name"
            aria-invalid={errors.cognome !== undefined}
            aria-describedby={
              errors.cognome !== undefined ? 'onboarding-pa-cognome-error' : undefined
            }
            {...register('cognome')}
          />
          {errors.cognome !== undefined && (
            <small id="onboarding-pa-cognome-error" className="form-text text-danger">
              {t('onboarding.pa.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-pa-cf" className="form-label">
            {t('onboarding.pa.fields.codice_fiscale')}
          </label>
          <input
            id="onboarding-pa-cf"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={16}
            className="form-control"
            aria-invalid={errors.codice_fiscale !== undefined}
            aria-describedby={
              errors.codice_fiscale !== undefined
                ? 'onboarding-pa-cf-error'
                : 'onboarding-pa-cf-hint'
            }
            {...register('codice_fiscale')}
          />
          <small id="onboarding-pa-cf-hint" className="form-text text-muted">
            {t('onboarding.pa.fields.codice_fiscale_hint')}
          </small>
          {errors.codice_fiscale !== undefined && (
            <small id="onboarding-pa-cf-error" className="form-text text-danger">
              {t('onboarding.pa.errors.codice_fiscale_invalid')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-pa-tel" className="form-label">
            {t('onboarding.pa.fields.telefono_optional')}
          </label>
          <input
            id="onboarding-pa-tel"
            type="tel"
            className="form-control"
            autoComplete="tel"
            {...register('telefono')}
          />
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.pa.actions.next')}
          backLabel={t('onboarding.pa.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step2Po(props: StepFormBaseProps<OnboardingPaStep2Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingPaStep2Body>(onboardingPaStep2Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingPaStep2Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.pa.steps.s2.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-pa-form-step-2"
      >
        <div className="form-group">
          <label htmlFor="onboarding-pa-po" className="form-label">
            {t('onboarding.pa.fields.po_riferimento')}
          </label>
          <input
            id="onboarding-pa-po"
            type="text"
            className="form-control"
            aria-invalid={errors.po_riferimento !== undefined}
            aria-describedby={
              errors.po_riferimento !== undefined
                ? 'onboarding-pa-po-error'
                : 'onboarding-pa-po-hint'
            }
            {...register('po_riferimento')}
          />
          <small id="onboarding-pa-po-hint" className="form-text text-muted">
            {t('onboarding.pa.fields.po_riferimento_hint')}
          </small>
          {errors.po_riferimento !== undefined && (
            <small id="onboarding-pa-po-error" className="form-text text-danger">
              {t('onboarding.pa.errors.field_required')}
            </small>
          )}
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.pa.actions.next')}
          backLabel={t('onboarding.pa.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step3Fondo(props: StepFormBaseProps<OnboardingPaStep3Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingPaStep3Body>(onboardingPaStep3Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingPaStep3Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.pa.steps.s3.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-pa-form-step-3"
      >
        <fieldset className="form-group">
          <legend className="form-label">{t('onboarding.pa.fields.fondo_principale')}</legend>
          {FONDO_PRINCIPALE_VALUES.map((value) => {
            const id = `onboarding-pa-fondo-${value.replace(/[^A-Za-z0-9]/gu, '_')}`;
            return (
              <div key={value} className="form-check">
                <input
                  id={id}
                  type="radio"
                  className="form-check-input"
                  value={value}
                  {...register('fondo_principale')}
                />
                <label htmlFor={id} className="form-check-label">
                  {value}
                </label>
              </div>
            );
          })}
          {errors.fondo_principale !== undefined && (
            <small className="form-text text-danger">
              {t('onboarding.pa.errors.field_required')}
            </small>
          )}
        </fieldset>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.pa.actions.next')}
          backLabel={t('onboarding.pa.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step4Ruolo(props: StepFormBaseProps<OnboardingPaStep4Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingPaStep4Body>(onboardingPaStep4Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingPaStep4Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.pa.steps.s4.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-pa-form-step-4"
      >
        <div className="form-group">
          <label htmlFor="onboarding-pa-ruolo" className="form-label">
            {t('onboarding.pa.fields.ruolo_adg')}
          </label>
          <input
            id="onboarding-pa-ruolo"
            type="text"
            className="form-control"
            aria-invalid={errors.ruolo_adg !== undefined}
            {...register('ruolo_adg')}
          />
          {errors.ruolo_adg !== undefined && (
            <small className="form-text text-danger">
              {t('onboarding.pa.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-pa-exp" className="form-label">
            {t('onboarding.pa.fields.esperienza_anni')}
          </label>
          <input
            id="onboarding-pa-exp"
            type="number"
            min={0}
            max={80}
            className="form-control"
            aria-invalid={errors.esperienza_anni !== undefined}
            {...register('esperienza_anni')}
          />
          {errors.esperienza_anni !== undefined && (
            <small className="form-text text-danger">
              {t('onboarding.pa.errors.esperienza_anni_invalid')}
            </small>
          )}
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.pa.actions.next')}
          backLabel={t('onboarding.pa.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step5Consent(props: StepFormBaseProps<OnboardingPaStep5Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingPaStep5Body>(onboardingPaStep5Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingPaStep5Body>({
    resolver,
    defaultValues: {
      consent_privacy: defaultValues.consent_privacy ?? false,
      consent_ai_act: defaultValues.consent_ai_act ?? false,
    } as OnboardingPaStep5Body,
  });

  return (
    <StepCard title={t('onboarding.pa.steps.s5.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-pa-form-step-5"
      >
        <p className="mb-3 small text-muted">{t('onboarding.pa.steps.s5.intro')}</p>
        <div className="form-check mb-2">
          <input
            id="onboarding-pa-consent-privacy"
            type="checkbox"
            className="form-check-input"
            aria-invalid={errors.consent_privacy !== undefined}
            aria-describedby={
              errors.consent_privacy !== undefined
                ? 'onboarding-pa-consent-privacy-error'
                : undefined
            }
            {...register('consent_privacy')}
          />
          <label htmlFor="onboarding-pa-consent-privacy" className="form-check-label">
            {t('onboarding.pa.fields.consent_privacy')}
          </label>
          {errors.consent_privacy !== undefined && (
            <small
              id="onboarding-pa-consent-privacy-error"
              className="form-text text-danger d-block"
            >
              {t('onboarding.pa.errors.consent_required')}
            </small>
          )}
        </div>
        <div className="form-check mb-3">
          <input
            id="onboarding-pa-consent-ai"
            type="checkbox"
            className="form-check-input"
            aria-invalid={errors.consent_ai_act !== undefined}
            aria-describedby={
              errors.consent_ai_act !== undefined ? 'onboarding-pa-consent-ai-error' : undefined
            }
            {...register('consent_ai_act')}
          />
          <label htmlFor="onboarding-pa-consent-ai" className="form-check-label">
            {t('onboarding.pa.fields.consent_ai_act')}
          </label>
          {errors.consent_ai_act !== undefined && (
            <small id="onboarding-pa-consent-ai-error" className="form-text text-danger d-block">
              {t('onboarding.pa.errors.consent_required')}
            </small>
          )}
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.pa.actions.finalize')}
          backLabel={t('onboarding.pa.actions.back')}
        />
      </form>
    </StepCard>
  );
}

interface OnboardingPaLoadingState {
  readonly kind: 'loading';
}
interface OnboardingPaErrorState {
  readonly kind: 'error';
  readonly title: string;
  readonly detail: string | null;
}
interface OnboardingPaReadyState {
  readonly kind: 'ready';
  readonly session: OnboardingPaSessionDto;
  readonly activeStep: OnboardingPaStepNumber;
}
interface OnboardingPaDoneState {
  readonly kind: 'done';
  readonly session: OnboardingPaStepResponseDto;
}

type OnboardingPaState =
  | OnboardingPaLoadingState
  | OnboardingPaErrorState
  | OnboardingPaReadyState
  | OnboardingPaDoneState;

function nextStep(current: number): OnboardingPaStepNumber {
  const candidate = Math.min(current + 1, ONBOARDING_PA_TOTAL_STEPS);
  return Math.max(1, candidate) as OnboardingPaStepNumber;
}

function clampActiveStep(currentStep: number, requested: number): OnboardingPaStepNumber {
  const maxAllowed = Math.min(ONBOARDING_PA_TOTAL_STEPS, currentStep + 1);
  const safe = Math.max(1, Math.min(requested, maxAllowed));
  return safe as OnboardingPaStepNumber;
}

export function OnboardingPaPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const subject = principal?.subject ?? '';

  const [state, setState] = useState<OnboardingPaState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const stepperSteps = useMemo<readonly OnboardingStepperStep[]>(
    () => [
      { number: 1, label: t('onboarding.pa.steps.s1.label') },
      { number: 2, label: t('onboarding.pa.steps.s2.label') },
      { number: 3, label: t('onboarding.pa.steps.s3.label') },
      { number: 4, label: t('onboarding.pa.steps.s4.label') },
      { number: 5, label: t('onboarding.pa.steps.s5.label') },
    ],
    [t],
  );

  useEffect(() => {
    if (!isAuthenticated || subject.length === 0) {
      return;
    }
    const cancellation = { cancelled: false };
    void (async (): Promise<void> => {
      try {
        const snapshot = loadOnboardingPaSnapshot(subject);
        const { session } = await createOnboardingPaSession();
        if (cancellation.cancelled) {
          return;
        }
        saveOnboardingPaSnapshot(snapshotFromSessionDto(subject, session));
        const completed =
          typeof session.completed_at === 'string' && session.completed_at.length > 0;
        if (completed) {
          setState({ kind: 'done', session });
          return;
        }
        const resumed =
          snapshot !== null && snapshot.sessionId === session.session_id
            ? clampActiveStep(session.current_step, snapshot.currentStep + 1)
            : nextStep(session.current_step);
        setState({ kind: 'ready', session, activeStep: resumed });
      } catch (err) {
        if (cancellation.cancelled) {
          return;
        }
        const apiErr = err instanceof OnboardingApiError ? err : null;
        setState({
          kind: 'error',
          title: apiErr?.message ?? t('onboarding.pa.errors.bootstrap_failed'),
          detail: apiErr?.detail ?? null,
        });
      }
    })();
    return (): void => {
      cancellation.cancelled = true;
    };
  }, [isAuthenticated, subject, t]);

  const persistStep = useCallback(
    async (stepNumber: OnboardingPaStepNumber, body: OnboardingPaStepBody): Promise<void> => {
      if (state.kind !== 'ready') {
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const updated = await submitOnboardingPaStep(state.session.session_id, stepNumber, body);
        const completed =
          typeof updated.completed_at === 'string' && updated.completed_at.length > 0;
        saveOnboardingPaSnapshot({
          subject,
          sessionId: updated.session_id,
          currentStep: updated.current_step,
          completed,
        });
        if (completed) {
          clearOnboardingPaSnapshot();
          setState({ kind: 'done', session: updated });
          return;
        }
        setState({
          kind: 'ready',
          session: updated,
          activeStep: nextStep(updated.current_step),
        });
      } catch (err) {
        const apiErr = err instanceof OnboardingApiError ? err : null;
        setSubmitError(
          apiErr?.detail ?? apiErr?.message ?? t('onboarding.pa.errors.submit_failed'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [state, subject, t],
  );

  const handleBack = useCallback((): void => {
    setSubmitError(null);
    setState((prev) => {
      if (prev.kind !== 'ready') {
        return prev;
      }
      if (prev.activeStep <= 1) {
        return prev;
      }
      const target = (prev.activeStep - 1) as OnboardingPaStepNumber;
      return { kind: 'ready', session: prev.session, activeStep: target };
    });
  }, []);

  if (!isAuthenticated) {
    return (
      <section className="urania-onboarding-pa" data-testid="onboarding-pa-unauthenticated">
        <h1 className="display-5 mb-3">{t('onboarding.pa.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('onboarding.pa.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('onboarding.pa.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary" data-testid="onboarding-pa-go-to-login">
            {t('onboarding.pa.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-onboarding-pa" data-testid="onboarding-pa-page">
      <nav className="mb-3">
        <Link to="/dashboard" className="small text-decoration-none">
          ← {t('dashboard.title')}
        </Link>
      </nav>
      <div
        className="rounded mb-3 px-3 px-md-4 py-2 py-md-3 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-1"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.65rem',
              letterSpacing: '0.05em',
            }}
          >
            ONBOARDING · FUNZIONARIO AdG
          </span>
          <h1 className="h5 mb-1">{t('onboarding.pa.title')}</h1>
          <p
            className="mb-0 opacity-90 small"
            style={{ fontSize: '0.78rem' }}
          >
            {t('onboarding.pa.subtitle')}
          </p>
        </div>
      </div>

      <OnboardingStepper
        steps={stepperSteps}
        currentStep={state.kind === 'ready' ? state.activeStep : ONBOARDING_PA_TOTAL_STEPS}
        ariaLabel={t('onboarding.pa.stepper_aria_label')}
        counterLabel={(c, n) => t('onboarding.pa.stepper_counter', { current: c, total: n })}
      />

      <div className="urania-onboarding-pa__form">
        {submitError !== null && (
        <div className="alert alert-danger" role="alert" data-testid="onboarding-pa-submit-error">
          {submitError}
        </div>
      )}

      {state.kind === 'loading' && (
        <p data-testid="onboarding-pa-loading">{t('onboarding.pa.loading')}</p>
      )}

      {state.kind === 'error' && (
        <div
          className="alert alert-danger"
          role="alert"
          data-testid="onboarding-pa-bootstrap-error"
        >
          <strong>{state.title}</strong>
          {state.detail !== null && <p className="mb-0">{state.detail}</p>}
        </div>
      )}

      {state.kind === 'done' && (
        <div className="alert alert-success" role="status" data-testid="onboarding-pa-completed">
          <h2 className="h4 mb-2">{t('onboarding.pa.done.title')}</h2>
          <p className="mb-0">
            {t('onboarding.pa.done.body', {
              outboxEventId: state.session.outbox_event_id ?? 'n/a',
            })}
          </p>
        </div>
      )}

      {state.kind === 'ready' && (
        <div data-testid={`onboarding-pa-step-${String(state.activeStep)}`}>
          {state.activeStep === 1 && (
            <Step1Anagrafica
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(1, body)}
              onBack={null}
              submitting={submitting}
            />
          )}
          {state.activeStep === 2 && (
            <Step2Po
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(2, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 3 && (
            <Step3Fondo
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(3, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 4 && (
            <Step4Ruolo
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(4, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 5 && (
            <Step5Consent
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(5, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
        </div>
      )}
      </div>
    </section>
  );
}

// `/onboarding/pa` (redirect a `/onboarding`, fusione A.4.3 — Riordino
// della mappa delle rotte §4). `OnboardingPaPage` resta esportata: la
// consuma `routes/onboarding/index.tsx`, che sceglie fra questa e
// `OnboardingRiPage` in base al ruolo — "wizard unico, si adatta al
// ruolo", non due wizard su due URL diverse.
export const Route = createRoute({
  getParentRoute: () => ServiziSegmentRoute,
  path: '/onboarding/pa',
  beforeLoad: () => {
    throw redirect({ to: '/onboarding', replace: true });
  },
});
